import React from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, Tooltip, Typography } from 'glints-aries/es/@next';
import { Blue } from 'glints-aries/es/@next/utilities/colors';
import { space4, space12 } from 'glints-aries/es/@next/utilities/spacing';
import { compact } from 'lodash-es';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ASSET_TYPES } from '../../../common/constants';
import MultiFileUploader from './components/MultiFileUploader/MultiFileUploader';
import { useIsViewOnlyMode } from './FormContext';
import { CompanyVerificationFormState } from './types';

const MAX_ADDITIONAL_DOCUMENTS = 5;

type Props = {
  fieldName: FieldPath<CompanyVerificationFormState>;
  disabled?: boolean;
};

export default function VerificationAdditionalDocumentsUploader({
  fieldName,
  disabled,
}: Props) {
  const isViewOnly = useIsViewOnlyMode();
  const { control } = useFormContext<CompanyVerificationFormState>();
  const additionalDocuments = compact(
    useWatch({
      control,
      name: fieldName,
    }) as string[]
  );
  const { formatMessage } = useIntl();

  if (isViewOnly && !additionalDocuments.length) return null;

  return (
    <Flex flexDirection="column" gap={space12}>
      <Flex gap={space4} alignItems="center">
        <Typography variant="body2">
          <FormattedMessage
            id="text-additional-documents"
            defaultMessage="Additional Documents"
          />
        </Typography>
        <Tooltip
          content={formatMessage({
            id: 'text-any-additional-documents',
            defaultMessage:
              'Any additional documents that may help expedite verification, such as SIUP, NIB, photos of business activities, or a practice license (if applicable).',
          })}
          zIndex={1004}
        >
          <Icon
            name="ri-information-line"
            width={24}
            height={24}
            fill={Blue.S99}
          />
        </Tooltip>
      </Flex>
      <MultiFileUploader.List
        hideTitle={true}
        title={<></>}
        assetType={ASSET_TYPES.COMPANY_ADDITIONAL_DOCUMENT}
        fieldName={fieldName}
        maxImages={MAX_ADDITIONAL_DOCUMENTS}
        disabled={disabled}
      />
    </Flex>
  );
}
